import { Fragment, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { items } from "./HeaderItems";
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  useDisclosure,
  Stack,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerBody,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  VStack,
  Divider,
  Box,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  AppearanceCaption,
  AppearanceControl,
} from "components/UIComponents/AppearanceControl";
import LanguageSelect from "../../UIComponents/LanguageSelect";
import { useSelector } from "react-redux";
import { UserAvatar } from "./UserAvatar";
import { RootState } from "redux/store";
import { FormattedMessage, useIntl } from "react-intl";
import { useMobileContext } from "context/MobileContext";
import "./Header.css";
import { FaThList } from "react-icons/fa";
import LogoComponent from "components/UIComponents/LogoComponent";
import { logoutUser } from "utils/auth/logout";

const MobileNavbar = () => {
  const intl = useIntl();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isUserLoggedIn = useSelector(
    (state: RootState) => state.userData.isUserLoggedIn
  );

  const pathname = useLocation().pathname;

  const initialRef = useRef(null);
  const finalRef = useRef(null);

  // Ends the server session, clears the local one, reloads to the home page.
  const onLogout = () => {
    void logoutUser();
  };

  return (
    <div className="mobile-navbar">
      <Stack direction="row" mb="4">
        <Link to="/">
          <LogoComponent />
        </Link>
      </Stack>
      <Button
        colorScheme="blue"
        onClick={onOpen}
        aria-label={intl.formatMessage({ id: "OpenMenuLabel" })}
      >
        <FaThList />
      </Button>
      <Drawer placement={"right"} onClose={onClose} isOpen={isOpen}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerBody className="mobile-drawer-body">
            <div className="menu-items mobile">
              <div className="mobile-drawer-actions">
                {isUserLoggedIn && (
                  <div className="user-avatar" onClick={onClose}>
                    <UserAvatar size="md" />
                  </div>
                )}
                <LanguageSelect />
              </div>
              <Box px={2} py={2}>
                <AppearanceCaption />
                <AppearanceControl />
              </Box>
              {items.map((item) =>
                item.path ? (
                  <Link
                    aria-disabled
                    key={`index-${item.id}`}
                    to={item.path}
                    className="header-item"
                    onClick={onClose}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <Fragment key={`fragment-${item.id}`}>
                    {item.children?.map((element) => (
                      <Link
                        to={element.path || "/"}
                        key={element.path}
                        className="header-item"
                        onClick={onClose}
                      >
                        {element.label}
                      </Link>
                    ))}{" "}
                  </Fragment>
                )
              )}
              {pathname !== "/auth/log-in" && !isUserLoggedIn ? (
                <Link to="/auth/log-in">
                  <Button colorScheme="blue">
                    <FormattedMessage id="LogInText" />
                  </Button>
                </Link>
              ) : (
                <Button
                  colorScheme="blue"
                  w="30%"
                  mt={14}
                  onClick={onLogout}
                >
                  <FormattedMessage id="LogoutButton" />
                </Button>
              )}
            </div>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
      <Modal
        initialFocusRef={initialRef}
        finalFocusRef={finalRef}
        isOpen={isModalOpen}
        onClose={onClose}
        isCentered={true}
        size={"xs"}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader textAlign="center" pb={0}>
            <FormattedMessage id="LogoutConfirmTitle" />
            <br />
            <FormattedMessage id="LogoutConfirmMessage" />
          </ModalHeader>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={onLogout}>
              <FormattedMessage id="LogoutButton" />
            </Button>
            <Button onClick={() => setIsModalOpen(false)}>
              <FormattedMessage id="CancelButton" />
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

const Header = () => {
  const [isSticky, setIsSticky] = useState<boolean>(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isPopoverOpen, onOpen: onPopoverOpen, onClose: onPopoverClose } = useDisclosure();

  const { isMobile } = useMobileContext();

  const isUserLoggedIn = useSelector(
    (state: RootState) => state.userData.isUserLoggedIn
  );

  const pathname = useLocation().pathname;

  // Ends the server session, clears the local one, reloads to the home page.
  const onLogout = () => {
    void logoutUser();
  };


  useEffect(() => {
    const onScroll = (e) => {
      if (window.scrollY >= 30) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [isSticky]);

  // Popover row hover is the surface-hover role. The popover ground is the
  // opaque bg.default (white light / #22303c dark): Chakra's own dark popover
  // (#2d3748) was the same shade as the hover, so the row state was invisible.
  const hoverBg = "bg.surfaceHover";

  // The header is not rendered on /auth/* for a logged-out visitor, so the
  // appearance control is offered there in its compact form instead.
  const isHeaderHidden = pathname.split("/").includes("auth") && !isUserLoggedIn;

  return (
    <>
      {isHeaderHidden && (
        <Box position="absolute" top={4} right={4} zIndex="docked">
          <AppearanceControl variant="menu" />
        </Box>
      )}
      {!isHeaderHidden && (
        !isMobile ? (
          <header
            className={`header ${isSticky ? "header-scrolled" : ""}`}
          >
        <div className="header-inner">
          <Link to="/">
            <LogoComponent />
          </Link>
          <div className="menu-items">
            {items.map((item) =>
              item.path ? (
                <Link
                  aria-disabled
                  key={item.id}
                  to={item.path}
                  className="header-item"
                >
                  {item.label}
                </Link>
              ) : (
                <Menu key={item.id}>
                  <MenuButton className="header-item">
                    <FormattedMessage id="ToolsNavbarText" />{" "}
                    <ChevronDownIcon />
                  </MenuButton>
                  <MenuList>
                    {item.children?.map((element) => (
                      <Link to={element.path || "/"} key={element.id}>
                        <MenuItem>{element.label}</MenuItem>
                      </Link>
                    ))}
                  </MenuList>
                </Menu>
              )
            )}
          </div>
          <div className="header-right-section">
            {isUserLoggedIn ? (
              <Popover 
                trigger="hover" 
                placement="bottom-end"
                isOpen={isPopoverOpen}
                onOpen={onPopoverOpen}
                onClose={onPopoverClose}
              >
                <PopoverTrigger>
                  <Box cursor="pointer">
                    <UserAvatar size="md" />
                  </Box>
                </PopoverTrigger>
                <PopoverContent width="220px" zIndex={9999} bg="bg.default">
                  <PopoverBody p={0}>
                    <VStack spacing={0} align="stretch">
                      <Link to="/profile/info" className="profile-popover-link" onClick={onPopoverClose}>
                        <Box px={4} py={2} _hover={{ bg: hoverBg }} cursor="pointer">
                          <FormattedMessage id="ProfileProfileText" />
                        </Box>
                      </Link>
                      <Link to="/profile/balance" className="profile-popover-link" onClick={onPopoverClose}>
                        <Box px={4} py={2} _hover={{ bg: hoverBg }} cursor="pointer">
                          <FormattedMessage id="ProfileBalanceText" />
                        </Box>
                      </Link>
                      <Link to="/profile/subscriptions" className="profile-popover-link" onClick={onPopoverClose}>
                        <Box px={4} py={2} _hover={{ bg: hoverBg }} cursor="pointer">
                          <FormattedMessage id="ProfileSubscriptionsText" />
                        </Box>
                      </Link>
                      <Link to="/profile/api" className="profile-popover-link" onClick={onPopoverClose}>
                        <Box px={4} py={2} _hover={{ bg: hoverBg }} cursor="pointer">
                          <FormattedMessage id="ProfileApiText" />
                        </Box>
                      </Link>
                      <Divider my={2} />
                      <Box p={2} display="flex" flexDirection="row" alignItems="center" gap={2}>
                        <Box flex={1}>
                          <LanguageSelect />
                        </Box>
                        <AppearanceControl variant="toggles" />
                      </Box>
                      <Divider />
                      <Box p={2}>
                        <Button 
                          colorScheme="red" 
                          size="sm" 
                          width="100%"
                          onClick={() => {
                            onOpen();
                          }}
                        >
                          <FormattedMessage id="ProfileLogoutText" />
                        </Button>
                      </Box>
                    </VStack>
                  </PopoverBody>
                </PopoverContent>
              </Popover>
            ) : (
              pathname !== "/auth/log-in" && (
                <Popover 
                  trigger="hover" 
                  placement="bottom-end"
                  isOpen={isPopoverOpen}
                  onOpen={onPopoverOpen}
                  onClose={onPopoverClose}
                >
                  <PopoverTrigger>
                    <Button colorScheme="blue" size="md">
                      <FormattedMessage id="LogInText" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent width="220px" zIndex={9999} bg="bg.default">
                    <PopoverBody p={0}>
                      <VStack spacing={0} align="stretch">
                        <Link to="/auth/log-in" className="profile-popover-link" onClick={onPopoverClose}>
                          <Box px={4} py={2} _hover={{ bg: hoverBg }} cursor="pointer">
                            <FormattedMessage id="LogInText" />
                          </Box>
                        </Link>
                        <Divider my={2} />
                        <Box p={2} display="flex" flexDirection="row" alignItems="center" gap={2}>
                          <Box flex={1}>
                            <LanguageSelect />
                          </Box>
                          <AppearanceControl variant="toggles" />
                        </Box>
                      </VStack>
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
              )
            )}
          </div>
        </div>
      </header>
        ) : (
          <MobileNavbar />
        )
      )}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        isCentered={true}
        size={"xs"}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader textAlign="center" pb={0}>
            <FormattedMessage id="LogoutConfirmTitle" />
            <br />
            <FormattedMessage id="LogoutConfirmMessage" />
          </ModalHeader>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={onLogout}>
              <FormattedMessage id="LogoutButton" />
            </Button>
            <Button onClick={onClose}>
              <FormattedMessage id="CancelButton" />
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default Header;
